import {
    Alert,
    AlertTitle,
    Box,
    Button,
    ButtonBase,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TableContainer from '@mui/material/TableContainer';
import DataTable from '../../components/DataTable/DataTable';
import SVCCContractActionDialog from '../Presentaciones/components/SVCCContractActionDialog';
import {
    ChipInformativo,
    EstadoCuitSvccChip,
    EstadoCuitSvccChipValue,
    PALETA_ESTADO_CUIT_SVCC
} from '../Presentaciones/components/PresentationStateChip';
import { AVISO_NEUTRO } from './components/VigenciaBadge';
import HallazgosEstablecimiento from '../Presentaciones/components/HallazgosEstablecimiento';
import SVCCDetalleCuitDrawer from './components/SVCCDetalleCuitDrawer';
import PlazosCelda from './components/PlazosCelda';
import NovedadesChip from './components/NovedadesChip';
import PeriodoCelda from './components/PeriodoCelda';
import MenuAccionesFila from './components/MenuAccionesFila';
import { ESTILO_TABLA_PRESENTACIONES } from '../Presentaciones/estiloTablaPresentaciones';
import type { AccionMenuFila } from './components/MenuAccionesFila';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import usePresentacionesSVCC from './hooks/usePresentacionesSVCC';
import type { EstadoCuitSvcc, ResumenValidacionCuit } from './hooks/usePresentacionesSVCC';
import { informesConNovedades } from './hooks/usePresentacionesSVCC';

const BADGES: Array<{ key: 'TODAS' | EstadoCuitSvcc; label: string }> = [
    { key: 'TODAS', label: 'Todas' },
    { key: 'SIN_INICIAR', label: 'Sin iniciar' },
    { key: 'RECHAZADA', label: 'Con errores' },
    { key: 'PRESENTADA_SIN_CONSOLIDAR', label: 'Lista para consolidar' },
    { key: 'CONSOLIDADA', label: 'Consolidadas' }
];

/**
 * Segunda línea de la celda de estado. Sólo dice cosas que se derivan del
 * estado mismo — el detalle por establecimiento (en qué anexo frenó, cuántos
 * errores) vive en el drawer, que es el único que tiene esos datos.
 */
const SUBLINEA_ESTADO: Partial<Record<EstadoCuitSvccChipValue, string>> = {
    // "No se completó" y no "la SRT rechazó": RECHAZADA es cualquier intento con
    // ERROR antes de consolidar, incluidos timeouts y errores nuestros.
    RECHAZADA: 'El envío no se completó — ver el proceso para el detalle',
    PRESENTADA_SIN_CONSOLIDAR: 'Los anexos fueron aceptados · falta consolidar',
    DESINCRONIZADA: 'NO reintentar'
};

/**
 * Alto y tipografía de TODOS los botones de la columna de acciones.
 *
 * Estaban despareja: los que pasan por `BotonAccion` salían en 28px (por el
 * `size="small"`) y los `contained` crudos —Iniciar presentación, Consolidar,
 * Modificar— en 33px, con otra tipografía. Apilados uno debajo del otro, la
 * diferencia se lee como un error de maquetado.
 *
 * 32px y no 28: el botón principal de la fila con 28 queda apretado.
 */
const ESTILO_BOTON_ACCION = {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    height: 32,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.2,
    borderRadius: '8px'
} as const;

/**
 * Por qué quedan cerrados Iniciar presentación, Modificar y Notificar cliente
 * cuando el contrato está dado de baja. Uno solo para los tres: es el mismo
 * motivo, y repetido a mano se desincroniza.
 */
/** Lo mismo, en pocas palabras, para el ítem deshabilitado del menú ⋮. */
const MOTIVO_CONTRATO_DE_BAJA_CORTO = 'Contrato dado de baja';

/** Botón secundario de la fila: borde gris y texto oscuro, el principal manda. */
const ESTILO_BOTON_SECUNDARIO = {
    flexShrink: 0,
    px: 1.5,
    borderColor: '#C9D6D9',
    color: '#1E272E',
    '&:hover': { borderColor: '#9AA5A9', backgroundColor: '#F3F8F8' }
} as const;

/** Por qué Iniciar presentación está cerrado mientras el cliente corrige. */
const MOTIVO_ESPERANDO_AL_CLIENTE =
    'Todos los informes de esta fila están en borrador: el cliente los está corrigiendo y todavía no los reenvió. No hay nada para enviar a la SRT.';

/**
 * La SRT rechazó el Inicio con FU01: el CUIT ya tenía una presentación abierta.
 * Se reconoce por el código o por el texto, que viaja tal cual en `motivoRechazo`.
 */
const esTramitePrevioAbierto = (motivoRechazo: string | null | undefined): boolean =>
    /\[FU01\]|presentaci[oó]n previa sin finalizar/i.test(motivoRechazo ?? '');

const MOTIVO_CONTRATO_DE_BAJA =
    'El contrato está dado de baja: no se puede presentar ante la SRT ni notificar al cliente en nombre de un contrato terminado.';

/**
 * Tooltip sobre un botón deshabilitado.
 *
 * MUI no dispara eventos de mouse sobre un botón `disabled`, así que el tooltip
 * no aparecería nunca sin un elemento vivo alrededor. Sin `motivo` devuelve el
 * hijo tal cual: envolver siempre agregaría un `span` en cada botón de la
 * grilla, y `fullWidth` mide contra el padre.
 */
const ConMotivo = ({ motivo, children }: { motivo?: string; children: ReactElement }) =>
    motivo ? (
        <Tooltip title={motivo}>
            <span style={{ display: 'block', width: '100%' }}>{children}</span>
        </Tooltip>
    ) : (
        children
    );

/**
 * Línea de estado debajo de los botones: una sola frase con un punto del
 * color de lo que dice (rojo frena, ámbar avisa, gris informa). Reemplaza
 * los párrafos sueltos que apilaba cada estado.
 */
const AvisoAccion = ({
    tono,
    children,
    testId
}: {
    tono: 'error' | 'aviso' | 'neutro';
    children: ReactNode;
    testId?: string;
}) => {
    const color = {
        error: '#B42318',
        aviso: '#B76E00',
        neutro: '#6B777C'
    }[tono];
    return (
        <Box
            data-testid={testId}
            sx={{
                display: 'flex',
                gap: 0.75,
                alignItems: 'flex-start',
                fontSize: 12,
                lineHeight: 1.4,
                color
            }}
        >
            <Box
                component="span"
                aria-hidden
                sx={{
                    width: 6,
                    height: 6,
                    mt: '5px',
                    borderRadius: '50%',
                    bgcolor: color,
                    flexShrink: 0
                }}
            />
            <Box component="span">{children}</Box>
        </Box>
    );
};
/**
 * Celda de acción: una fila de botones (secundario + principal + menú ⋮)
 * y debajo los avisos. Antes cada acción era un botón a todo el ancho,
 * apilados, y la fila crecía a 220 px.
 */
const CeldaAccion = ({
    cuit,
    botones,
    menu = [],
    children
}: {
    cuit: string;
    botones: ReactNode;
    menu?: AccionMenuFila[];
    children?: ReactNode;
}) => (
    <Box display="flex" flexDirection="column" gap={0.75}>
        <Box display="flex" gap={0.75} alignItems="center">
            {botones}
            <MenuAccionesFila cuit={cuit} acciones={menu} />
        </Box>
        {children}
    </Box>
);

const formatearCuit = (cuit: string) =>
    /^\d{11}$/.test(cuit) ? `${cuit.slice(0, 2)}-${cuit.slice(2, 10)}-${cuit.slice(10)}` : cuit;

const formatearFecha = (fecha: string | null) => {
    if (!fecha) return null;
    const valor = new Date(fecha);
    if (Number.isNaN(valor.getTime())) return null;
    return valor.toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};

interface PresentacionesSVCCIndexProps {
    /**
     * Saca el título y el padding propios, que los aporta la pantalla que la
     * aloja. Hoy el único consumidor es la tab SVCC de Presentaciones de
     * backoffice; la prop se mantiene opcional para poder montar la grilla sola
     * (así corren los tests) sin arrastrar el layout de esa pantalla.
     */
    embebido?: boolean;
    /**
     * CUIT del contrato elegido en la barra de filtros. Sin valor, se listan
     * todos los CUIT del alcance del usuario.
     */
    cuitContrato?: string;
    /**
     * Organización elegida en la barra de filtros. Sólo la aplica el backend
     * para perfil BACKOFFICE; el resto ya queda acotado a sus organismos.
     */
    idOrganizacion?: number;
}

const PresentacionesSVCCIndex = ({
    embebido = false,
    cuitContrato,
    idOrganizacion
}: PresentacionesSVCCIndexProps) => {
    const {
        // `cuits` sin filtrar: el drawer no se tiene que cerrar porque el
        // operador cambie el buscador o el badge mientras lo tiene abierto.
        cuits,
        cuitsPagina,
        totalCuitsFiltrados,
        paginacion,
        setPaginacion,
        isLoading,
        badgeSeleccionado,
        setBadgeSeleccionado,
        conteos,
        vincularCuit,
        vinculandoCuit,
        bloqueadosVincular,
        nombresPorInforme,
        cerrarBloqueadosVincular,
        resultadosVerificacion,
        validacionesPorCuit,
        detalleValidacionPorCuit,
        validandoCuits,
        cancelandoCuit,
        cancelarPresentacion,
        modificandoCuit,
        modificarPresentacion,
        notificandoClienteCuit,
        notificarCliente,
        dialogConsolidarCuit,
        abrirConsolidar,
        cerrarConsolidar,
        handleConsolidar,
        isConsolidando,
        handleSincronizarNovedades,
        isSincronizandoNovedades
    } = usePresentacionesSVCC(cuitContrato, idOrganizacion);

    /**
     * De los diálogos y el drawer se guarda sólo el CUIT, y la fila se
     * re-deriva de la lista en cada render. Guardar el item era un snapshot:
     * después de cancelar o reenviar, la grilla refetcheaba pero el drawer
     * seguía mostrando el estado viejo y ofrecía acciones sobre un trámite que
     * ya no existía (cancelar dos veces → 400 de la SRT).
     */
    const [cuitDetalle, setCuitDetalle] = useState<string | null>(null);
    const [cuitAModificar, setCuitAModificar] = useState<string | null>(null);
    const [cuitReenvio, setCuitReenvio] = useState<string | null>(null);
    /*
     * "Notificar cliente" manda un mail y una notificación al cliente pidiéndole
     * que regularice. Con un click suelto salía sin querer; ahora confirma, como
     * "Notificar corrección" del lado del cliente.
     */
    const [cuitANotificar, setCuitANotificar] = useState<string | null>(null);

    /*
     * La grilla pagina y filtra en el servidor, así que `cuits` es sólo la
     * página vigente. El drawer y los diálogos se abren desde una fila de esa
     * página, pero el operador puede cambiar el badge o la página con el drawer
     * abierto: sin este recuerdo, la fila desaparece y el drawer se cierra solo
     * en medio de una operación.
     */
    const filasVistas = useRef<Record<string, any>>({});

    /**
     * Una fila por CUIT × período: sólo la activa —la que el backend marca con
     * `periodoActivo`— acciona el trámite del CUIT. Las demás son de dos
     * clases: EN ESPERA (tiene informes sin enviar y va en la próxima
     * presentación, cuando cierre la activa) o cerrada (ya se envió todo lo
     * suyo). Ninguna ofrece acciones: las acciones son del CUIT y las presta la
     * fila activa; el drawer y los diálogos también se abren sobre ella. Con
     * un backend anterior `periodoActivo` no viene y toda fila es activa.
     */
    const esFilaActiva = (item: any) => item.periodoActivo !== false;
    const estaEnEspera = (item: any) =>
        !esFilaActiva(item) && (item.informesSinEnviar ?? 0) + (item.informesEnBorrador ?? 0) > 0;

    /**
     * Línea "en corrección por el cliente" de la celda de estado: el informe
     * volvió a borrador después de pasar por la SRT y el CUIT sigue en la
     * grilla por eso. Sin esta línea la fila decía "0 para enviar" y no se
     * entendía qué esperaba.
     */
    const EnCorreccion = ({ item }: { item: any }) => {
        const cantidad = item.informesEnBorrador ?? 0;
        if (!cantidad) return null;
        return (
            <Typography
                variant="body2"
                data-testid={`en-correccion-${item.cuit}-${item.anioPeriodo}`}
                sx={{ color: 'warning.dark', fontWeight: 600 }}
            >
                {cantidad === 1
                    ? '1 informe en corrección por el cliente'
                    : `${cantidad} informes en corrección por el cliente`}
            </Typography>
        );
    };

    /**
     * Las filas de un CUIT van juntas (así las ordena el backend) y comparten
     * la celda de CUIT / Empresa: una sola celda que abarca sus períodos, en
     * vez de repetir el CUIT o escribir "mismo CUIT" en cada fila. La primera
     * fila del grupo la dibuja con `rowSpan`; las demás no la dibujan.
     */
    const filasDelCuit = (cuit: string) => cuits.filter((fila) => fila.cuit === cuit);
    const rowSpanCuit = (item: any) => {
        const grupo = filasDelCuit(item.cuit);
        if (grupo.length < 2) return undefined;
        return grupo[0] === item ? grupo.length : 0;
    };

    useEffect(() => {
        cuits.filter(esFilaActiva).forEach((fila) => {
            filasVistas.current[fila.cuit] = fila;
        });
    }, [cuits]);

    const filaPorCuit = (cuit: string | null) =>
        cuit
            ? (cuits.find((fila) => fila.cuit === cuit && esFilaActiva(fila)) ??
              filasVistas.current[cuit] ??
              null)
            : null;

    const detalleCuit = filaPorCuit(cuitDetalle);
    const cuitAReenviar = filaPorCuit(cuitReenvio);

    /**
     * El estado DESINCRONIZADA se deriva del resultado de la verificación
     * contra la SRT, y sólo es confiable si describe la misma presentación que
     * esta fila muestra: `getRegistroByCuit` (backend) no filtra por período,
     * así que puede haber consultado otra presentación.
     *
     * OJO: se quitó el botón "Verificar con SRT", así que hoy nada dispara esa
     * verificación desde esta pantalla y el estado no se alcanza. El mecanismo
     * queda entero en el hook (`verificarSincronizacion`) para volver a
     * engancharlo — por ejemplo al abrir el drawer o desde un chequeo
     * periódico.
     */
    const estaDesincronizada = (item: any) => {
        const resultado = resultadosVerificacion[item.cuit];
        return Boolean(
            resultado?.desincronizada && resultado.idPresentacionSrt === item.idPresentacionSrt
        );
    };

    /**
     * Estado que se muestra. `DESINCRONIZADA` no viene del backend: se deriva
     * de la verificación contra la SRT, y cuando aplica pisa al estado local
     * porque el local es justamente el que quedó mal.
     */
    const estadoVisible = (item: any): EstadoCuitSvccChipValue =>
        estaDesincronizada(item) ? 'DESINCRONIZADA' : (item.estado as EstadoCuitSvccChipValue);

    /**
     * Botón de validar (lote o pendientes). El "Revisa N establecimientos"
     * que lo acompañaba va como aviso gris debajo, junto con el resto.
     */
    const BotonValidar = ({ item, variante }: { item: any; variante: 'lote' | 'pendientes' }) => {
        const etiqueta = variante === 'lote' ? 'Validar lote' : 'Validar pendientes';
        return (
            <Tooltip
                title={
                    variante === 'lote'
                        ? 'Abre el detalle y valida cada establecimiento contra las reglas de la SRT antes de enviar. Un error frena el lote entero.'
                        : 'Abre el detalle y valida los informes que no entraron en la presentación consolidada: es lo que va a la Modificación.'
                }
            >
                <Button
                    size="small"
                    variant="outlined"
                    aria-label={`${etiqueta} — CUIT ${item.cuit}`}
                    onClick={() => setCuitDetalle(item.cuit)}
                    sx={{ ...ESTILO_BOTON_ACCION, ...ESTILO_BOTON_SECUNDARIO }}
                >
                    {etiqueta}
                </Button>
            </Tooltip>
        );
    };

    const detalleValidar = (item: any, variante: 'lote' | 'pendientes') => {
        const cantidad =
            variante === 'lote' ? item.cantidadEstablecimientos : item.establecimientosSinPresentar;
        if (variante === 'lote') {
            return `Revisa ${cantidad} ${
                cantidad === 1 ? 'establecimiento' : 'establecimientos'
            } antes de enviar`;
        }
        return cantidad === 1
            ? 'Revisa el informe que quedó afuera'
            : `Revisa los ${cantidad} informes que quedaron afuera`;
    };

    /**
     * Por qué el envío está cerrado (o por qué está abierto sin veredicto).
     * Mismas tres situaciones que antes; sólo cambia el formato.
     */
    const MotivoBloqueoValidacion = ({
        bloqueado,
        sinVerificar,
        esperando,
        resumen
    }: {
        bloqueado: boolean;
        sinVerificar: boolean;
        esperando: boolean;
        resumen?: ResumenValidacionCuit;
    }) => {
        if (esperando) {
            return (
                <AvisoAccion tono="neutro">
                    Verificando el lote antes de habilitar el envío
                </AvisoAccion>
            );
        }

        if (bloqueado && resumen && !('error' in resumen)) {
            const { bloqueantes } = resumen;
            return (
                <AvisoAccion tono="error">
                    <b>{`${bloqueantes} ${
                        bloqueantes === 1 ? 'error bloqueante' : 'errores bloqueantes'
                    }`}</b>
                    {' — abrí Validar lote para ver el detalle'}
                </AvisoAccion>
            );
        }

        /*
         * No se pudo verificar: el envío queda habilitado a propósito. Decirlo es
         * necesario, porque un botón habilitado sin aviso se lee como "el lote
         * está limpio".
         */
        if (sinVerificar) {
            return (
                <AvisoAccion tono="aviso">
                    No se pudo verificar el lote — validá antes de enviar
                </AvisoAccion>
            );
        }

        return null;
    };

    /**
     * `items` memoizado: pasarlo como literal creaba un objeto nuevo en cada
     * render, y con la validación por CUIT resolviendo aparte eso hacía que
     * `DataTable` reemplazara las filas en vez de reusarlas.
     */
    const itemsTabla = useMemo(() => ({ data: { data: cuitsPagina } }), [cuitsPagina]);

    const renderAccion = (item: any) => {
        /*
         * El contrato dado de baja no vacía la columna: cierra las acciones que
         * hablan en nombre del contrato terminado —abrir un trámite ante la SRT
         * o escribirle al cliente— y deja las que cierran lo que ya está
         * abierto. Consolidar y Cancelar en SRT siguen disponibles: si el CUIT
         * quedó con un trámite abierto, hay que poder terminarlo.
         *
         * Antes la fila entera quedaba gris y sin ninguna acción —visto en
         * DROGUERIA VARADERO, con el contrato terminado y todos sus
         * establecimientos de baja—, lo que dejaba ese trámite sin salida.
         */
        const contratoDeBaja = item.contratoActivo === false;

        if (!esFilaActiva(item)) {
            // La fila cerrada no dice nada: su estado y su ID ya lo cuentan.
            if (!estaEnEspera(item)) return null;
            return (
                <Typography
                    variant="body2"
                    data-testid={`sin-accion-${item.cuit}-${item.anioPeriodo}`}
                    sx={{ color: 'text.secondary', fontSize: 12 }}
                >
                    {item.periodoEnCurso != null
                        ? `Se gestiona desde la fila de ${item.periodoEnCurso}.`
                        : 'Se gestiona desde la fila del período en curso.'}
                </Typography>
            );
        }

        const estado = estadoVisible(item);
        const vinculando = vinculandoCuit === item.cuit;
        const resumenValidacion = validacionesPorCuit[item.cuit];
        const sinVerificar = !resumenValidacion || 'error' in resumenValidacion;
        const bloqueadoPorValidacion =
            !!resumenValidacion &&
            !('error' in resumenValidacion) &&
            resumenValidacion.bloqueantes > 0;
        /*
         * El envío se cierra por dos motivos distintos, y el botón los muestra
         * distinto:
         *
         * - `bloqueadoPorValidacion`: bloqueantes confirmados. Queda cerrado y la
         *   leyenda de abajo dice cuántos son.
         * - `esperandoValidacion`: la validación del lote todavía viaja. Va el
         *   spinner EN el botón —el progreso donde está la acción, no sólo en la
         *   leyenda— y se cierra mientras dura: un spinner sobre un botón
         *   clickeable se lee como "ya podés enviar", y enviar sin el veredicto
         *   termina en rechazo de la SRT y deja el CUIT trabado con un trámite
         *   abierto que hay que cancelar a mano.
         *
         * Si la validación FALLA el envío queda habilitado: "no se pudo
         * verificar" no es "las validaciones fallaron", y ahí la red de seguridad
         * es el 400 de `vincular` con `bloqueados`, que abre el diálogo de
         * errores.
         */
        const esperandoValidacion = validandoCuits && !resumenValidacion;
        const cancelando = cancelandoCuit === item.cuit;
        const modificando = modificandoCuit === item.cuit;
        const notificando = notificandoClienteCuit === item.cuit;

        const accionVerProceso: AccionMenuFila = {
            texto: 'Ver proceso',
            etiqueta: `Ver proceso — CUIT ${item.cuit}`,
            onClick: () => setCuitDetalle(item.cuit)
        };
        const accionNotificar: AccionMenuFila = {
            texto: 'Notificar cliente',
            etiqueta: `Notificar cliente — CUIT ${item.cuit}`,
            deshabilitada: contratoDeBaja || notificando,
            cargando: notificando,
            motivo: contratoDeBaja ? MOTIVO_CONTRATO_DE_BAJA_CORTO : undefined,
            onClick: () => setCuitANotificar(item.cuit)
        };
        const accionCancelar = (motivo?: string): AccionMenuFila => ({
            texto: 'Cancelar en SRT',
            etiqueta: `Cancelar en SRT — CUIT ${item.cuit}`,
            destructiva: true,
            deshabilitada: cancelando || !!motivo,
            cargando: cancelando,
            motivo,
            onClick: () => cancelarPresentacion(item.cuit)
        });

        /*
         * Todos los informes de la fila están en borrador, corrigiéndose: no hay
         * nada listo para enviar. La validación del lote devuelve 0 bloqueantes
         * porque no encuentra informes, y sin este corte el botón quedaba
         * habilitado para mandar un lote vacío a la SRT. Sólo con el dato del
         * backend (`informesEnBorrador`): con uno anterior no viene y no se
         * bloquea nada.
         */
        const esperandoAlCliente =
            (item.informesEnBorrador ?? 0) > 0 && (item.informesSinEnviar ?? 0) === 0;

        const botonIniciar = (onClick: () => void) => (
            <ConMotivo
                motivo={
                    contratoDeBaja
                        ? MOTIVO_CONTRATO_DE_BAJA
                        : esperandoAlCliente
                          ? MOTIVO_ESPERANDO_AL_CLIENTE
                          : undefined
                }
            >
                <Button
                    fullWidth
                    variant="contained"
                    aria-label={`Iniciar presentación — CUIT ${item.cuit}`}
                    disabled={
                        contratoDeBaja ||
                        esperandoAlCliente ||
                        vinculando ||
                        bloqueadoPorValidacion ||
                        esperandoValidacion
                    }
                    startIcon={
                        vinculando || esperandoValidacion ? (
                            <CircularProgress size={14} />
                        ) : undefined
                    }
                    onClick={onClick}
                    sx={ESTILO_BOTON_ACCION}
                >
                    Iniciar presentación
                </Button>
            </ConMotivo>
        );

        const motivoBloqueo = esperandoAlCliente ? (
            <AvisoAccion tono="aviso" testId={`esperando-cliente-${item.cuit}`}>
                Esperando que el cliente reenvíe los informes corregidos
            </AvisoAccion>
        ) : (
            <MotivoBloqueoValidacion
                bloqueado={bloqueadoPorValidacion}
                sinVerificar={sinVerificar}
                esperando={esperandoValidacion}
                resumen={resumenValidacion}
            />
        );

        if (estado === 'DESINCRONIZADA') {
            return (
                <Alert
                    severity="warning"
                    variant="outlined"
                    icon={false}
                    data-testid={`desincronizada-${item.cuit}`}
                    sx={{ py: 0, px: 1.25, borderRadius: '8px' }}
                >
                    <Typography variant="body2">
                        La SRT consolidó pero falló el guardado local. Requiere intervención manual.
                    </Typography>
                </Alert>
            );
        }

        if (estado === 'SIN_INICIAR') {
            return (
                <CeldaAccion
                    cuit={item.cuit}
                    botones={
                        <>
                            <BotonValidar item={item} variante="lote" />
                            {botonIniciar(() => vincularCuit(item.cuit))}
                        </>
                    }
                >
                    <AvisoAccion tono="neutro">{detalleValidar(item, 'lote')}</AvisoAccion>
                    {motivoBloqueo}
                </CeldaAccion>
            );
        }

        if (estado === 'RECHAZADA') {
            /**
             * Sin `idPresentacionSrt` no hay trámite abierto en la SRT (el
             * rechazo ocurrió antes de obtener el id, o el payload no pasó la
             * validación local): no hay nada que cancelar y el CUIT se puede
             * enviar de cero. Bloquear también este caso dejaba la fila sin
             * ninguna acción posible — "Cancelar en SRT" falla sin id.
             *
             * Reenviar pide confirmación: el intento anterior fue rechazado y
             * mandar lo mismo sin corregir vuelve a fallar.
             */
            if (item.idPresentacionSrt == null) {
                return (
                    <CeldaAccion
                        cuit={item.cuit}
                        botones={
                            <>
                                <BotonValidar item={item} variante="lote" />
                                {botonIniciar(() => setCuitReenvio(item.cuit))}
                            </>
                        }
                        menu={[accionVerProceso, accionNotificar]}
                    >
                        <AvisoAccion tono="neutro">{detalleValidar(item, 'lote')}</AvisoAccion>
                        {motivoBloqueo}
                        <AvisoAccion tono="neutro">
                            No hay presentación abierta en la SRT
                        </AvisoAccion>
                    </CeldaAccion>
                );
            }

            /**
             * Con `idPresentacionSrt` hay un trámite abierto que bloquea el
             * CUIT: la SRT no admite sumar nada a una presentación cuyo estado
             * ya cambió (Disposición SRT 1/2020) — hay que cancelar y volver a
             * mandar todo de cero. Reenviar sin cancelar frena otra vez el lote
             * entero, y no hay override manual a propósito. Iniciar queda a la
             * vista, cerrado, para que se lea qué está trabado.
             */
            /*
             * FU01 "presentación previa sin finalizar": la SRT rechazó el Inicio
             * porque el CUIT ya tenía un trámite abierto, no por los datos. No hay
             * nada que el cliente tenga que corregir: se cancela y se vuelve a
             * iniciar.
             */
            const tramitePrevioAbierto = esTramitePrevioAbierto(item.motivoRechazo);
            return (
                <CeldaAccion
                    cuit={item.cuit}
                    botones={
                        <>
                            {/*
                              * Sin `fullWidth`, como "Validar lote" en las otras
                              * filas: con él (y `flexShrink: 0`) se quedaba con
                              * el ancho y cortaba "Iniciar presentación".
                              */}
                            <Button
                                size="small"
                                variant="outlined"
                                aria-label={`Ver proceso — CUIT ${item.cuit}`}
                                onClick={() => setCuitDetalle(item.cuit)}
                                sx={{ ...ESTILO_BOTON_ACCION, ...ESTILO_BOTON_SECUNDARIO }}
                            >
                                Ver proceso
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                disabled
                                aria-label={`Iniciar presentación — CUIT ${item.cuit}`}
                                sx={ESTILO_BOTON_ACCION}
                            >
                                Iniciar presentación
                            </Button>
                        </>
                    }
                    menu={[accionCancelar(), accionNotificar]}
                >
                    <AvisoAccion tono="error">
                        {tramitePrevioAbierto
                            ? 'Trámite previo abierto en la SRT: cancelalo desde ⋮ para volver a iniciar'
                            : 'Bloqueado hasta que el cliente corrija'}
                    </AvisoAccion>
                </CeldaAccion>
            );
        }

        if (estado === 'PRESENTADA_SIN_CONSOLIDAR') {
            /*
             * Consolidar necesita el IdPresentacion de la SRT: sin trámite
             * abierto el backend tira antes de llamarla. Por lo mismo Cancelar
             * queda cerrado sin id (el endpoint devuelve 400).
             */
            const sinTramite = item.idPresentacionSrt == null;
            return (
                <CeldaAccion
                    cuit={item.cuit}
                    botones={
                        <Button
                            fullWidth
                            variant="contained"
                            aria-label={`Consolidar presentación — CUIT ${item.cuit}`}
                            disabled={sinTramite}
                            onClick={() => abrirConsolidar(item.cuit)}
                            color="success"
                            sx={ESTILO_BOTON_ACCION}
                        >
                            Consolidar presentación
                        </Button>
                    }
                    menu={[accionCancelar(sinTramite ? 'Sin presentación abierta' : undefined)]}
                >
                    <AvisoAccion tono={sinTramite ? 'neutro' : 'aviso'}>
                        {sinTramite
                            ? 'Sin presentación abierta en la SRT: no hay nada que cancelar'
                            : 'Última chance de cancelar: consolidar es irreversible'}
                    </AvisoAccion>
                </CeldaAccion>
            );
        }

        if (estado === 'CONSOLIDADA') {
            /*
             * Sin informes afuera no hay nada que validar: consolidar dejó todo
             * en PRESENTADO y el drawer no llama a validaciones. Modificar abre
             * un trámite NUEVO ante la SRT (Anexo IV §2: Inicio + métodos de
             * modificación + Consolidación), así que con el contrato terminado
             * queda cerrado por el mismo motivo que Iniciar presentación.
             */
            const conPendientes = item.establecimientosSinPresentar > 0;
            return (
                <CeldaAccion
                    cuit={item.cuit}
                    botones={
                        <>
                            {conPendientes && <BotonValidar item={item} variante="pendientes" />}
                            <ConMotivo
                                motivo={contratoDeBaja ? MOTIVO_CONTRATO_DE_BAJA : undefined}
                            >
                                <Button
                                    fullWidth
                                    variant="contained"
                                    aria-label={`Modificar presentación — CUIT ${item.cuit}`}
                                    disabled={contratoDeBaja || modificando}
                                    startIcon={
                                        modificando ? <CircularProgress size={14} /> : undefined
                                    }
                                    onClick={() => setCuitAModificar(item.cuit)}
                                    sx={ESTILO_BOTON_ACCION}
                                >
                                    Modificar presentación
                                </Button>
                            </ConMotivo>
                        </>
                    }
                >
                    {conPendientes && (
                        <AvisoAccion tono="aviso">{detalleValidar(item, 'pendientes')}</AvisoAccion>
                    )}
                </CeldaAccion>
            );
        }

        return undefined;
    };

    return (
        <Box
            bgcolor="#ffffff"
            borderRadius="12px"
            p={embebido ? 2 : 3}
            data-testid="grilla-svcc-por-cuit"
        >
            {/*
             * Embebida en la tab SVCC de Presentaciones el título propio
             * duplicaría el header de esa pantalla; la subida con el período sí
             * se mantiene porque dice qué se está mirando.
             */}
            {!embebido && (
                <Typography variant="h6" mb={0.5}>
                    Presentaciones SVCC por CUIT
                </Typography>
            )}

            {/*
             * Chip de MUI con la misma paleta que el chip de estado de la fila.
             * El seleccionado usa el primary del theme, que es el color de
             * acción del resto de la app (azul en backoffice, verde en SIMP).
             */}
            <Box
                role="group"
                aria-label="Filtrar por estado"
                display="flex"
                gap={0.5}
                mb={1.5}
                flexWrap="wrap"
            >
                {BADGES.map((badge) => {
                    const seleccionado = badgeSeleccionado === badge.key;
                    const paleta =
                        badge.key === 'TODAS' ? null : PALETA_ESTADO_CUIT_SVCC[badge.key];

                    return (
                        <ButtonBase
                            key={badge.key}
                            data-testid={`badge-${badge.key}`}
                            aria-pressed={seleccionado}
                            onClick={() => setBadgeSeleccionado(badge.key)}
                            sx={{
                                height: 32,
                                px: 1.5,
                                gap: 0.75,
                                borderRadius: '999px',
                                fontSize: 13,
                                fontWeight: 600,
                                fontFamily: 'inherit',
                                // Color de la organización (theme), no un verde fijo:
                                // en backoffice el primario es otro.
                                color: seleccionado ? 'primary.main' : '#3D4A50',
                                bgcolor: (theme) =>
                                    seleccionado
                                        ? alpha(theme.palette.primary.main, 0.08)
                                        : 'transparent',
                                '&:hover': {
                                    bgcolor: (theme) =>
                                        seleccionado
                                            ? alpha(theme.palette.primary.main, 0.12)
                                            : '#F3F8F8'
                                }
                            }}
                        >
                            {badge.label}{' '}
                            <Box
                                component="span"
                                sx={{
                                    minWidth: 20,
                                    height: 20,
                                    px: 0.75,
                                    borderRadius: '999px',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    ...(seleccionado
                                        ? { bgcolor: 'primary.main', color: '#FFFFFF' }
                                        : paleta
                                          ? {
                                                bgcolor: paleta.backgorundColor,
                                                color: paleta.textColor
                                            }
                                          : { bgcolor: '#EEF2F3', color: '#3D4A50' })
                                }}
                            >
                                {conteos[badge.key]}
                            </Box>
                        </ButtonBase>
                    );
                })}
            </Box>

            {/*
             * Sin hover de fila: `DataTable/styles.module.css` lo pinta para
             * todas las grillas, pero acá la fila no se selecciona ni navega —
             * las acciones son los botones de la última columna, y el resaltado
             * sugería que la fila entera era clickeable. Se apaga sólo en esta
             * tabla, sin tocar el resto de las grillas.
             */}
            <TableContainer
                sx={{
                    /*
                     * Mismo lenguaje que el informe SVCC: tarjeta redondeada
                     * con borde suave en vez de la sombra del Paper, y header
                     * claro en vez del bloque del color secundario. Sólo esta
                     * grilla; `DataTable` sigue igual para el resto.
                     */
                    ...ESTILO_TABLA_PRESENTACIONES,
                    '& table tr:hover': { backgroundColor: 'transparent' },
                    // El cursor de mano venía del mismo lugar y sugería lo mismo.
                    '& table tbody tr': { cursor: 'default', outline: 'none' },
                    /*
                     * Aire vertical: el `DataTable` da 0,35rem y con celdas de
                     * cuatro líneas y tres botones apilados los bordes de la
                     * fila quedaban pegados al contenido. Sólo esta grilla.
                     */
                    '& table tbody td': {
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        verticalAlign: 'top',
                        borderBottom: '1px solid #E0EBED'
                    },
                    /*
                     * `DataTable/styles.module.css` pinta de blanco el hover del
                     * último botón de la celda (pensado para los icon-buttons de
                     * las otras grillas). Acá el último botón es un `contained`
                     * de texto blanco, así que al pasar el mouse quedaba blanco
                     * sobre blanco y la etiqueta desaparecía.
                     */
                    '& table tr td button.MuiButton-contained:not(.Mui-disabled):last-child:hover':
                        {
                            backgroundColor: 'primary.main'
                        },
                    '& table tr td button.MuiButton-outlined:not(.Mui-disabled):last-child:hover': {
                        backgroundColor: 'action.hover'
                    },
                    // Los íconos de la celda de período (RAR) son botones: misma regla.
                    '& table tr td button.MuiIconButton-root:not(.Mui-disabled):last-child:hover': {
                        backgroundColor: 'action.hover'
                    }
                }}
            >
                {/*
                 * columnTitles, columnWidths y headers tienen que quedar en el
                 * mismo orden y con la misma cantidad de entradas: el header
                 * sale de columnTitles y las celdas de cada fila de headers,
                 * así que si difieren la tabla queda corrida.
                 */}
                <DataTable
                    columnTitles={[
                        'CUIT / Empresa',
                        'Período',
                        'Estado',
                        'Plazos',
                        'Presentación SRT',
                        'Acción'
                    ]}
                    /*
                     * Período es la columna con más contenido (año, informes,
                     * nómina, botón, avisos): con 170px envolvía cada línea en
                     * dos y la celda se leía como un bloque. Toma ancho de la
                     * de CUIT, que es `auto`.
                     */
                    columnWidths={['auto', '235px', '185px', '175px', '140px', '330px']}
                    items={itemsTabla}
                    isLoading={isLoading}
                    /*
                     * Paginación con el contrato "remoto" de `DataTable`
                     * (`filters.skip`/`take` + `count`), pero cortando en el
                     * hook: el endpoint devuelve todos los CUIT de una y no
                     * acepta skip/take. `count` es el total filtrado, no el de
                     * la página, o el paginador muestra una sola página.
                     */
                    filters={paginacion}
                    setFilters={setPaginacion}
                    count={totalCuitsFiltrados}
                    onSelectRow={() => {}}
                    /*
                     * La fila del contrato dado de baja se lee como cualquier
                     * otra: su período, su estado y sus plazos siguen siendo
                     * información válida, y grisar la fila entera los volvía
                     * ilegibles. Lo que no se puede hacer se cierra botón por
                     * botón en `renderAccion`, y la baja se anuncia en la celda
                     * de CUIT / Empresa.
                     */
                    selected={null}
                    selectedItemFromAction={null}
                    menuOpen={false}
                    headers={[
                        {
                            type: 'data',
                            rowSpan: rowSpanCuit,
                            /*
                             * La empresa primero y en negrita: es lo que se busca
                             * con la vista. El CUIT va debajo, chico, y sigue
                             * abriendo el detalle SRT.
                             */
                            upperLabel: (item: any) => (
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: '#1E272E', lineHeight: 1.35 }}
                                >
                                    {item.nombreOrganizacion ?? '—'}
                                </Typography>
                            ),
                            lowerLabel: (item: any) => {
                                const periodos = filasDelCuit(item.cuit).length;
                                return (
                                    <Box sx={{ mt: 0.25 }}>
                                        <Button
                                            size="small"
                                            variant="text"
                                            aria-label={`Ver detalle SRT — CUIT ${item.cuit}`}
                                            onClick={() => setCuitDetalle(item.cuit)}
                                            sx={{
                                                p: 0,
                                                minWidth: 0,
                                                textTransform: 'none',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                fontVariantNumeric: 'tabular-nums',
                                                '&:hover': {
                                                    bgcolor: 'transparent',
                                                    textDecoration: 'underline'
                                                }
                                            }}
                                        >
                                            {formatearCuit(item.cuit)}
                                        </Button>
                                        {/*
                                         * Con varios períodos la celda es del CUIT
                                         * entero, así que cuenta períodos: los
                                         * establecimientos de cada uno los dice
                                         * su fila ("2024 · 2 informes SVCC").
                                         */}
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            {periodos > 1
                                                ? `${periodos} períodos`
                                                : `${item.cantidadEstablecimientos} ${
                                                      item.cantidadEstablecimientos === 1
                                                          ? 'establecimiento'
                                                          : 'establecimientos'
                                                  }`}
                                        </Typography>
                                        {/*
                                         * Sin esto la fila sólo decía "Consolidada" y
                                         * un informe cargado después de consolidar
                                         * quedaba invisible: hay que abrir el drawer
                                         * para descubrirlo. Consolidar es
                                         * irreversible, así que ese informe necesita
                                         * Modificación — conviene verlo en la grilla.
                                         */}
                                        {item.establecimientosSinPresentar > 0 && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    display: 'block',
                                                    color: 'warning.dark',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {`${item.establecimientosSinPresentar} sin presentar`}
                                            </Typography>
                                        )}
                                        {/*
                                         * La baja del contrato va con la identidad
                                         * de la fila y no con el estado: el estado
                                         * es el del trámite ante la SRT, que sigue
                                         * su propio curso aunque el contrato haya
                                         * terminado. Es informativo — lo que no se
                                         * puede hacer se ve en los botones.
                                         */}
                                        {item.contratoActivo === false && (
                                            <Chip
                                                size="small"
                                                label="Contrato dado de baja"
                                                data-testid="svcc-contrato-de-baja"
                                                sx={{
                                                    mt: 0.5,
                                                    height: 20,
                                                    fontSize: 11,
                                                    fontWeight: 600
                                                }}
                                            />
                                        )}
                                    </Box>
                                );
                            }
                        },
                        {
                            /*
                             * De qué año habla la fila y de dónde salió ese año.
                             * Sin esta columna la grilla mezclaba períodos sin
                             * decirlo: dos años son dos obligaciones, con su
                             * trámite y su plazo, y no había manera de saber
                             * cuál estaba mirando el operador.
                             */
                            type: 'data',
                            width: '235px',
                            // Todo junto en la celda: la cantidad de informes
                            // tiene que leerse ANTES del origen, y como
                            // `lowerLabel` se renderiza después quedaba al final.
                            upperLabel: (item: any) => <PeriodoCelda fila={item} />
                        },
                        {
                            type: 'data',
                            width: '185px',
                            upperLabel: (item: any) =>
                                estaEnEspera(item) ? (
                                    <ChipInformativo
                                        label="En espera"
                                        paleta={AVISO_NEUTRO}
                                        testId={`en-espera-${item.cuit}-${item.anioPeriodo}`}
                                    />
                                ) : (
                                    <EstadoCuitSvccChip estado={estadoVisible(item)} />
                                ),
                            lowerLabel: (item: any) => {
                                if (estaEnEspera(item)) {
                                    return (
                                        <>
                                            <Typography variant="body2">
                                                {item.periodoEnCurso != null
                                                    ? `Pendiente hasta que cierre la presentación de ${item.periodoEnCurso}`
                                                    : 'Pendiente hasta que cierre la presentación en curso'}
                                            </Typography>
                                            <EnCorreccion item={item} />
                                        </>
                                    );
                                }
                                const sublinea = SUBLINEA_ESTADO[estadoVisible(item)];
                                /*
                                 * El aviso de novedades va con el ESTADO y no con
                                 * la acción: no dice qué hacer, dice que lo que la
                                 * SRT tiene declarado dejó de coincidir con lo
                                 * cargado. Puede convivir con cualquier estado.
                                 */
                                // La validación es del CUIT: se muestra una vez,
                                // en la fila activa.
                                const novedades = esFilaActiva(item)
                                    ? informesConNovedades(validacionesPorCuit[item.cuit])
                                    : 0;
                                if (!sublinea && !novedades && !item.informesEnBorrador)
                                    return undefined;
                                return (
                                    <>
                                        <EnCorreccion item={item} />
                                        {sublinea && (
                                            /*
                                             * `body2` por lo mismo que la celda
                                             * de período: `caption` cae a 10,5px
                                             * y con el gris secundario la frase
                                             * que explica el estado no se lee.
                                             */
                                            <Typography variant="body2">{sublinea}</Typography>
                                        )}
                                        <NovedadesChip informes={novedades} />
                                    </>
                                );
                            }
                        },
                        {
                            /*
                             * Los dos plazos de la fila. Antes acá iba sólo la
                             * purga del borrador, que existe únicamente con
                             * trámite abierto: la columna quedaba vacía justo
                             * en las filas que todavía hay que enviar.
                             */
                            type: 'data',
                            width: '175px',
                            upperLabel: (item: any) => (
                                <PlazosCelda
                                    fila={item}
                                    vigenciaSuprimida={
                                        estadoVisible(item) === 'DESINCRONIZADA' ||
                                        !esFilaActiva(item)
                                    }
                                />
                            )
                        },
                        {
                            type: 'data',
                            width: '140px',
                            upperLabel: (item: any) => (
                                <Typography variant="body2" sx={{ display: 'block' }}>
                                    {item.idPresentacionSrt
                                        ? `ID ${item.idPresentacionSrt}`
                                        : esFilaActiva(item)
                                          ? 'Sin ID'
                                          : '—'}
                                </Typography>
                            ),
                            lowerLabel: (item: any) => {
                                // La fila en espera no tiene trámite propio: el
                                // intento que hereda es del CUIT y ya lo cuenta
                                // la fila activa (y "Ver proceso").
                                if (estaEnEspera(item)) return undefined;
                                const fecha = formatearFecha(item.fechaUltimoIntento);
                                if (!fecha) return undefined;
                                return (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                            display: 'block'
                                        }}
                                    >
                                        {`Último intento ${fecha}`}
                                    </Typography>
                                );
                            }
                        },
                        {
                            type: 'data',
                            width: '330px',
                            upperLabel: (item: any) => renderAccion(item)
                        }
                    ]}
                />
            </TableContainer>

            {detalleCuit && (
                <SVCCDetalleCuitDrawer
                    cuit={detalleCuit.cuit}
                    nombreOrganizacion={detalleCuit.nombreOrganizacion}
                    estado={estadoVisible(detalleCuit)}
                    idPresentacionSrt={detalleCuit.idPresentacionSrt}
                    fechaUltimoIntento={detalleCuit.fechaUltimoIntento}
                    fechaVencimientoEstimada={detalleCuit.fechaVencimientoEstimada}
                    cantidadEstablecimientos={detalleCuit.cantidadEstablecimientos}
                    onClose={() => setCuitDetalle(null)}
                    onIniciar={() =>
                        // Un reenvío tras un rechazo pasa por la misma
                        // confirmación que en la grilla.
                        detalleCuit.estado === 'RECHAZADA'
                            ? setCuitReenvio(detalleCuit.cuit)
                            : vincularCuit(detalleCuit.cuit)
                    }
                    onConsolidar={() => abrirConsolidar(detalleCuit.cuit)}
                    onCancelar={() => cancelarPresentacion(detalleCuit.cuit)}
                    onModificar={() => setCuitAModificar(detalleCuit.cuit)}
                    onSincronizarNovedades={() => handleSincronizarNovedades(detalleCuit.cuit)}
                    sincronizandoNovedades={isSincronizandoNovedades}
                    /*
                     * El detalle por informe ya vino con el resumen de la grilla:
                     * pasárselo evita que el drawer corra el mismo validador
                     * sobre los mismos informes al abrirse.
                     */
                    validacionesPrecargadas={detalleValidacionPorCuit[detalleCuit.cuit] ?? null}
                />
            )}

            {dialogConsolidarCuit && (
                <SVCCContractActionDialog
                    open
                    type="consolidate"
                    cuitPreseleccionado={dialogConsolidarCuit}
                    onClose={cerrarConsolidar}
                    onConfirm={handleConsolidar}
                    loading={isConsolidando}
                />
            )}

            {cuitAReenviar && (
                <Dialog open onClose={() => setCuitReenvio(null)} fullWidth maxWidth="sm">
                    <DialogTitle>Reenviar la presentación completa</DialogTitle>
                    <DialogContent dividers>
                        {cuitAReenviar.motivoRechazo && (
                            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
                                <AlertTitle sx={{ mb: 0.5 }}>
                                    El envío anterior fue rechazado
                                </AlertTitle>
                                <Typography variant="body2">
                                    {cuitAReenviar.motivoRechazo}
                                </Typography>
                            </Alert>
                        )}
                        <Typography variant="body2">
                            {`Se envían de cero los ${cuitAReenviar.cantidadEstablecimientos} establecimientos del CUIT ${formatearCuit(
                                cuitAReenviar.cuit
                            )}. Si no se corrigió lo que falló, el envío va a volver a fallar.`}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCuitReenvio(null)}>Cancelar</Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                vincularCuit(cuitAReenviar.cuit);
                                setCuitReenvio(null);
                            }}
                        >
                            Reenviar CUIT completo
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {cuitANotificar && (
                <Dialog
                    open
                    onClose={() => setCuitANotificar(null)}
                    fullWidth
                    maxWidth="sm"
                    data-testid="dialogo-notificar-cliente"
                >
                    <DialogTitle>¿Notificar al cliente?</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body2">
                            {`Le mandamos a ${
                                filaPorCuit(cuitANotificar)?.nombreOrganizacion ?? 'el cliente'
                            } (CUIT ${formatearCuit(
                                cuitANotificar
                            )}) un mail y una notificación pidiéndole que regularice la presentación: revisar las observaciones, corregir los datos y avisar con "Notificar corrección".`}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                            Hacelo cuando las observaciones estén cargadas en el proceso: es lo que
                            el cliente va a ver al entrar.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCuitANotificar(null)}>Cancelar</Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                notificarCliente(cuitANotificar);
                                setCuitANotificar(null);
                            }}
                        >
                            Notificar
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {cuitAModificar && (
                <Dialog open onClose={() => setCuitAModificar(null)} fullWidth maxWidth="sm">
                    <DialogTitle>Modificar presentación consolidada</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body2">
                            {`Se reenviarán a la SRT los datos corregidos del CUIT ${formatearCuit(
                                cuitAModificar
                            )} (Modificación de Anexo I / V / Nómina). La presentación sigue consolidada: el intento queda registrado en el historial.`}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCuitAModificar(null)}>Cancelar</Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                modificarPresentacion(cuitAModificar);
                                setCuitAModificar(null);
                            }}
                        >
                            Enviar modificación
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {bloqueadosVincular && (
                <Dialog open onClose={cerrarBloqueadosVincular} fullWidth maxWidth="md">
                    <DialogTitle>No se pudo vincular</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body2" sx={{ mb: 2, color: '#475569' }}>
                            {`No se envió nada a la SRT. ${bloqueadosVincular.length} establecimiento(s) tienen datos a corregir.`}
                        </Typography>

                        {bloqueadosVincular.map((b) => {
                            const bloqueantes = b.errores.filter(
                                (e) => e.severidad === 'error'
                            ).length;
                            const advertencias = b.errores.length - bloqueantes;

                            return (
                                <Box
                                    key={b.idInforme}
                                    sx={{
                                        mb: 2,
                                        p: 1.5,
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 1
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        {nombresPorInforme?.[b.idInforme] ??
                                            `Informe ${b.idInforme}`}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                        {`Informe ${b.idInforme} · ${bloqueantes} bloqueante(s)`}
                                        {advertencias > 0
                                            ? ` · ${advertencias} advertencia(s)`
                                            : ''}
                                    </Typography>
                                    <HallazgosEstablecimiento errores={b.errores} />
                                </Box>
                            );
                        })}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={cerrarBloqueadosVincular}>Cerrar</Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

export default PresentacionesSVCCIndex;
